import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquareText, Search, RefreshCw, AlertCircle } from "lucide-react";
import { answerDetailClicksApi } from "../api/client";
import { normalizeConversationMessage } from "../utils/conversationMessage";
import { fmtDate, fmtNumber } from "../utils/format";

const PAGE_SIZE = 20;

const ErrorBanner = ({ children }) => (
  <div
    style={{
      padding: "16px 20px",
      borderRadius: 12,
      border: "1px solid var(--red)",
      background: "#fff1f2",
      color: "var(--red)",
      marginBottom: 24,
      display: "flex",
      alignItems: "center",
      gap: 12,
      fontWeight: 500,
    }}
  >
    <AlertCircle size={20} /> {children}
  </div>
);

const Pagination = ({ page, pagination, onPageChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "14px 20px",
      borderTop: "1px solid var(--ink-100)",
      color: "var(--ink-500)",
      fontSize: 13,
    }}
  >
    <span>
      {pagination.total
        ? `${fmtNumber((page - 1) * pagination.limit + 1)}–${fmtNumber(Math.min(page * pagination.limit, pagination.total))} of ${fmtNumber(pagination.total)}`
        : "0 results"}
    </span>
    <div style={{ display: "flex", gap: 8 }}>
      <button className="btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        Previous
      </button>
      <button className="btn" disabled={page >= pagination.totalPages} onClick={() => onPageChange(page + 1)}>
        Next
      </button>
    </div>
  </div>
);

const AnswerContent = ({ content }) => {
  const { shortAnswer, longAnswer } = normalizeConversationMessage({
    role: "assistant",
    content: content || "",
  });
  const hasDetailedAnswer = Boolean(longAnswer);

  return (
    <div style={{ color: "var(--ink-900)", fontSize: 13, lineHeight: 1.55, overflowWrap: "anywhere" }}>
      {hasDetailedAnswer && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: "var(--ink-500)", fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Short answer</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{shortAnswer}</div>
        </div>
      )}
      {hasDetailedAnswer && (
        <div style={{ color: "var(--ink-500)", fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Detailed answer</div>
      )}
      <div style={{ whiteSpace: "pre-wrap" }}>{hasDetailedAnswer ? longAnswer : shortAnswer}</div>
    </div>
  );
};

export default function AnswerDetailClicks() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1, limit: PAGE_SIZE });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: targetPage, limit: PAGE_SIZE };
      const response = userId
        ? await answerDetailClicksApi.listForUser(userId, params)
        : await answerDetailClicksApi.users({ ...params, search });
      if (!response.success) throw new Error(response.error || "Unable to load detailed-answer clicks.");
      setData(response.data || []);
      setPagination(response.pagination);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Unable to load detailed-answer clicks.");
    } finally {
      setLoading(false);
    }
  }, [page, search, userId]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const changePage = (nextPage) => setPage(nextPage);
  const refresh = () => load(page);
  const title = userId ? "Detailed answers opened" : "Detailed answer clicks";

  return (
    <div style={{ width: "100%" }}>
      {userId && (
        <button
          type="button"
          onClick={() => navigate("/detailed-answer-clicks")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 24,
            padding: 0,
            border: 0,
            background: "transparent",
            color: "var(--ink-500)",
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={14} /> All users
        </button>
      )}

      <header style={{ marginBottom: 28 }}>
        <div className="t-eyebrow" style={{ color: "var(--ink-500)", letterSpacing: "0.05em", marginBottom: 8 }}>
          Sangathana Console / Analytics
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="t-display" style={{ fontSize: 34, margin: 0, color: "var(--ink-900)", overflowWrap: "anywhere" }}>
              {title}
            </h1>
            <p style={{ margin: "10px 0 0", color: "var(--ink-600)", fontFamily: "var(--serif)", fontSize: 16, fontStyle: "italic" }}>
              {userId ? (
                <><code style={{ fontFamily: "var(--mono)", fontSize: 12, fontStyle: "normal" }}>{userId}</code> · {fmtNumber(pagination.total)} answer expansions</>
              ) : (
                `${fmtNumber(pagination.total)} users with detailed-answer clicks`
              )}
            </p>
          </div>
          <button className="btn" type="button" onClick={refresh} disabled={loading} title="Refresh report">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {!userId && (
        <label style={{ display: "flex", alignItems: "center", gap: 10, maxWidth: 420, marginBottom: 20, padding: "0 12px", height: 42, border: "1px solid var(--ink-200)", borderRadius: 8, background: "#fff" }}>
          <Search size={16} color="var(--ink-400)" />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search user ID"
            aria-label="Search user ID"
            style={{ width: "100%", border: 0, outline: 0, background: "transparent", color: "var(--ink-800)", font: "inherit", fontSize: 14 }}
          />
        </label>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <section className="panel" style={{ background: "#fff", borderRadius: 12, border: "1px solid var(--ink-100)", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.02)", overflow: "hidden" }}>
        {userId ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1.4fr) minmax(200px, 1fr) 150px 190px", gap: 18, padding: "12px 20px", borderBottom: "1px solid var(--ink-100)", background: "var(--paper-2)", minWidth: 760 }}>
              {["User query", "Detailed answer", "Language", "Clicked at"].map((heading) => (
                <span key={heading} className="t-eyebrow" style={{ fontSize: 11, color: "var(--ink-500)" }}>{heading}</span>
              ))}
            </div>
            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={{ padding: 28, color: "var(--ink-500)" }}>Loading clicked answers…</div>
              ) : data.length ? data.map((click) => {
                const answer = click.answer_id;
                const conversation = click.conversation_id;
                return (
                  <article key={click._id} style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1.4fr) minmax(200px, 1fr) 150px 190px", gap: 18, padding: "18px 20px", borderBottom: "1px solid var(--ink-100)", minWidth: 760, alignItems: "start" }}>
                    <div style={{ color: "var(--ink-700)", fontSize: 13, lineHeight: 1.55, overflowWrap: "anywhere" }}>
                      {answer?.original_query || "Original query unavailable"}
                    </div>
                    <div>
                      {answer?.content ? <AnswerContent content={answer.content} /> : "Answer message unavailable"}
                      {conversation?._id && (
                        <div style={{ marginTop: 10 }}>
                          <Link to={`/conversations/${conversation._id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--saffron-deep)", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                            <MessageSquareText size={13} /> Open conversation
                          </Link>
                        </div>
                      )}
                    </div>
                    <div style={{ color: "var(--ink-600)", fontSize: 13 }}>{answer?.language || "—"}</div>
                    <time dateTime={click.createdAt} style={{ color: "var(--ink-600)", fontSize: 13 }}>{fmtDate(click.createdAt)}</time>
                  </article>
                );
              }) : (
                <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--ink-400)", fontFamily: "var(--serif)", fontStyle: "italic" }}>
                  No detailed-answer clicks found for this user.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) 150px 190px", gap: 18, padding: "12px 20px", borderBottom: "1px solid var(--ink-100)", background: "var(--paper-2)" }}>
              {["User ID", "Answer expansions", "Last clicked"].map((heading) => (
                <span key={heading} className="t-eyebrow" style={{ fontSize: 11, color: "var(--ink-500)" }}>{heading}</span>
              ))}
            </div>
            {loading ? (
              <div style={{ padding: 28, color: "var(--ink-500)" }}>Loading users…</div>
            ) : data.length ? data.map((user) => (
              <div key={user.user_id} style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) 150px 190px", gap: 18, padding: "16px 20px", borderBottom: "1px solid var(--ink-100)", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => navigate(`/detailed-answer-clicks/${encodeURIComponent(user.user_id)}`)}
                  style={{ justifySelf: "start", padding: 0, border: 0, background: "transparent", color: "var(--saffron-deep)", fontFamily: "var(--mono)", fontSize: 13, textAlign: "left", overflowWrap: "anywhere", cursor: "pointer" }}
                >
                  {user.user_id}
                </button>
                <span style={{ color: "var(--ink-900)", fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtNumber(user.total_clicks)}</span>
                <time dateTime={user.last_clicked_at} style={{ color: "var(--ink-600)", fontSize: 13 }}>{fmtDate(user.last_clicked_at)}</time>
              </div>
            )) : (
              <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--ink-400)", fontFamily: "var(--serif)", fontStyle: "italic" }}>
                No detailed-answer clicks found.
              </div>
            )}
          </>
        )}
        {!loading && pagination.total > 0 && (
          <Pagination page={page} pagination={pagination} onPageChange={changePage} />
        )}
      </section>
    </div>
  );
}